import { Control, useController, useWatch } from 'react-hook-form';
import {
  createRequiredValidator,
  createMaxLengthValidator,
  isValidInput,
} from '../../Campaigns/validators';
import { CspRegistrationFormData } from '../types';

export const useRegisterControllers = (
  control: Control<CspRegistrationFormData>
) => {
  // Watch the value of has2ndCnp to determine if secondary CNP fields are required
  const has2ndCnpValue = useWatch({ control, name: 'ui.has2ndCnp' });

  const {
    field: { value: market, onChange: setMarket },
    fieldState: { error: marketError },
  } = useController({
    control,
    name: 'register.market',
    rules: {
      validate: {
        isRequired: createRequiredValidator('Please select a market'),
      },
    },
  });

  const {
    field: {
      value: primaryCnpName,
      onChange: setPrimaryCnpName,
      onBlur: validatePrimaryCnpName,
    },
    fieldState: { error: primaryCnpNameError },
  } = useController({
    control,
    name: 'register.questionnairePrimaryCnpName',
    rules: {
      validate: {
        isValidInput,
        isRequired: createRequiredValidator('Please enter CNP/DCA name'),
        withValidMaxLength: createMaxLengthValidator(100),
      },
    },
  });

  const {
    field: {
      value: relationshipLength,
      onChange: setRelationshipLength,
      onBlur: validateRelationshipLength,
    },
    fieldState: { error: relationshipLengthError },
  } = useController({
    control,
    name: 'register.questionnaireRelationshipLength',
    rules: {
      validate: {
        isValidInput,
        isRequired: createRequiredValidator(
          'Please enter length of existing relationship'
        ),
        withValidMaxLength: createMaxLengthValidator(1000),
      },
    },
  });

  const {
    field: { value: has2ndCnp, onChange: setHas2ndCnp },
    fieldState: { error: has2ndCnpError },
  } = useController({
    control,
    name: 'ui.has2ndCnp',
    rules: {
      validate: {
        isRequired: createRequiredValidator(
          'Please specify if you have a secondary CNP or DCA'
        ),
      },
    },
  });

  const {
    field: {
      value: secondaryCnpName,
      onChange: setSecondaryCnpName,
      onBlur: validateSecondaryCnpName,
    },
    fieldState: { error: secondaryCnpNameError },
  } = useController({
    control,
    name: 'register.questionnaireSecondaryCnpName',
    disabled: has2ndCnpValue !== 'yes',
    rules: {
      validate: {
        isValidInput,
        isRequired: createRequiredValidator(
          'Please enter secondary CNP/DCA name'
        ),
        withValidMaxLength: createMaxLengthValidator(100),
      },
    },
  });

  const {
    field: {
      value: secondaryRelationshipLength,
      onChange: setSecondaryRelationshipLength,
      onBlur: validateSecondaryRelationshipLength,
    },
    fieldState: { error: secondaryRelationshipLengthError },
  } = useController({
    control,
    name: 'register.questionnaireRelationshipLengthSecondaryCNP',
    disabled: has2ndCnpValue !== 'yes',
    rules: {
      validate: {
        isValidInput,
        isRequired: createRequiredValidator(
          'Please enter length of existing relationship'
        ),
        withValidMaxLength: createMaxLengthValidator(1000),
      },
    },
  });

  const {
    field: { value: confirmCsp, onChange: setConfirmCsp },
    fieldState: { error: confirmCspError },
  } = useController({
    control,
    name: 'ui.confirmCsp',
    rules: {
      validate: {
        isRequired: (value) => value === true || 'You must confirm to proceed',
      },
    },
  });

  return {
    market,
    setMarket,
    marketError,
    primaryCnpName,
    setPrimaryCnpName,
    validatePrimaryCnpName,
    primaryCnpNameError,
    relationshipLength,
    setRelationshipLength,
    validateRelationshipLength,
    relationshipLengthError,
    has2ndCnp,
    setHas2ndCnp,
    has2ndCnpError,
    secondaryCnpName,
    setSecondaryCnpName,
    validateSecondaryCnpName,
    secondaryCnpNameError,
    secondaryRelationshipLength,
    setSecondaryRelationshipLength,
    validateSecondaryRelationshipLength,
    secondaryRelationshipLengthError,
    confirmCsp,
    setConfirmCsp,
    confirmCspError,
  };
};

export default useRegisterControllers;
